/**
 * Job Role Library PR 2: employer pay bands and market benchmarks (docs/job-role-benchmarks-contract.md sections 4 and 5).
 *
 * Three different values, never merged: (a) market benchmark observations (global reference data), (b) the employer-approved pay band
 * of a role (company data), (c) an employee's actual rate (Compensation module, never read or written by this feature). All enums travel
 * as STRINGS and money as JSON numbers; wireMapping.ts is the only place wire shapes are converted.
 */

/** The basis a band or an observation is stated in. */
export type PayBasis = 'Hourly' | 'Annual';

/** `benchmarkStatus` on a role: `NotPermitted` means the caller lacks the benchmark permission (it never leaks availability). */
export type BenchmarkStatus = 'Available' | 'NoSocCode' | 'NoData' | 'NotPermitted';

/** The status of a benchmark LOOKUP (the not-permitted case is a 403, not a status). */
export type BenchmarkLookupStatus = 'Available' | 'NoSocCode' | 'NoData';

export type BenchmarkGeographyType = 'National' | 'State' | 'Metro';
export type BenchmarkSourceKind = 'PublicGovernment' | 'Licensed' | 'Private' | 'Synthetic';
export type BenchmarkLicenseStatus = 'PendingReview' | 'PublicDomain' | 'OpenLicense' | 'Licensed' | 'NotPermitted';

/** The band as `GET /api/job-roles` and `GET /api/job-roles/{id}` carry it. */
export interface EmployerPayBandSummary {
  basis: PayBasis;
  minimum: number;
  midpoint: number;
  maximum: number;
  annualHoursAssumption: number;
  concurrencyVersion: number;
}

/** One employer pay band row (the current one when `effectiveToDate` is null). No user ids: the server does not resolve names here. */
export interface PayBand {
  id: string;
  roleId: string;
  basis: PayBasis;
  currency: string;
  minimum: number;
  midpoint: number;
  maximum: number;
  annualHoursAssumption: number;
  /** `yyyy-MM-dd` */
  effectiveFromDate: string;
  /** `yyyy-MM-dd`; null = the current band. */
  effectiveToDate: string | null;
  note: string | null;
  concurrencyVersion: number;
  createdAtUtc: string | null;
  acknowledgedAtUtc: string | null;
}

export interface SetPayBandInput {
  basis: PayBasis;
  minimum: number;
  midpoint: number;
  maximum: number;
  /** The server's default applies when omitted. */
  annualHoursAssumption?: number | undefined;
  /** `yyyy-MM-dd`, not in the future. */
  effectiveFromDate?: string | undefined;
  /** Must be true, or the server answers 400 `PAY_BAND_REVIEW_REQUIRED`. */
  reviewAcknowledged: boolean;
  note?: string | null | undefined;
  /** Optional snapshot of what the author viewed; never applied automatically. This release does not send one. */
  benchmarkContext?: unknown;
  /** Required when a current band exists (409 `CONCURRENCY_CONFLICT` when stale). */
  expectedConcurrencyVersion?: number | undefined;
}

export interface BenchmarkQuery {
  geographyType?: BenchmarkGeographyType | undefined;
  /** USPS state code; needed for State, and lets a Metro request fall back to its state. */
  state?: string | undefined;
  metroCode?: string | undefined;
  referenceYear?: number | undefined;
  basis?: PayBasis | undefined;
  /** 1..8784. The server echoes the value it used. */
  annualHours?: number | undefined;
}

export interface BenchmarkGeography {
  type: BenchmarkGeographyType;
  code: string | null;
  name: string;
}

export interface BenchmarkPoint {
  /** "Market Low", "Market Median" or "Market High". */
  label: string;
  /** The percentile the source really reports (10, 25, 50, 75 or 90). Never relabelled. */
  percentile: number;
  /** The value as the source states it, in `originalBasis`. */
  originalValue: number;
  /** The value in `convertedBasis` (equal to `originalValue` when no conversion was needed). */
  value: number;
}

export interface BenchmarkSourceResult {
  sourceCode: string;
  organization: string;
  title: string;
  url: string | null;
  kind: BenchmarkSourceKind;
  licenseStatus: BenchmarkLicenseStatus;
  attributionText: string | null;
  datasetEdition: string;
  referenceYear: number;
  retrievalDate: string;
  lastUpdatedUtc: string | null;
  expirationDate: string | null;
  requestedGeography: BenchmarkGeography;
  resolvedGeography: BenchmarkGeography;
  fallback: { applied: boolean; reason: string | null };
  currency: string;
  originalBasis: PayBasis;
  convertedBasis: PayBasis;
  points: BenchmarkPoint[];
  percentileDefinition: string | null;
  sampleSize: number | null;
  reliabilityNote: string | null;
  dataAvailability: 'Full' | 'Partial';
}

export interface BenchmarkResult {
  roleId: string;
  socCode: string | null;
  status: BenchmarkLookupStatus;
  message: string | null;
  request: {
    geographyType: BenchmarkGeographyType;
    state: string | null;
    metroCode: string | null;
    referenceYear: number | null;
    basis: PayBasis;
    annualHours: number;
    annualHoursSource: string;
  };
  sources: BenchmarkSourceResult[];
  excluded: { sourceCode: string; reason: string }[];
  /** Always `Unavailable` until an aggregation methodology is approved; the UI shows the reason and never a number. */
  suggestedRange: { status: string; reason: string | null };
  limitations: string[];
  disclaimer: string | null;
}

export interface BenchmarkSourceRegistryItem {
  code: string;
  organization: string;
  title: string;
  url: string | null;
  kind: BenchmarkSourceKind;
  licenseStatus: BenchmarkLicenseStatus;
  isEnabled: boolean;
  attributionText: string | null;
  usageNotes: string | null;
  latestBatch: {
    datasetEdition: string;
    referenceYear: number;
    retrievalDate: string;
    importedAtUtc: string;
    rowCount: number;
    status: string;
  } | null;
}

export const SYNTHETIC_DATA_LABEL = 'SYNTHETIC TEST DATA';
export const SYNTHETIC_DATA_EXPLANATION = 'These numbers are generated for testing. They are not market data.';

/** The acknowledgement the contract asks for before a pay band can be saved. */
export const PAY_BAND_REVIEW_STATEMENT =
  'I have reviewed this pay band against applicable minimum-wage, overtime, pay-transparency and equal-pay requirements';

export const COMPENSATION_DISCLAIMER =
  'Pay bands and market benchmarks are informational. They are not a guaranteed salary, legal advice, or a decision about wage-law or '
  + 'overtime requirements, and they never replace any minimum wage that applies. Your employer pay band is your own policy. A market '
  + 'benchmark is reference data from the source shown with it. Neither one sets any employee’s pay.';

export const DEFAULT_ANNUAL_HOURS = 2080;
export const MAX_ANNUAL_HOURS = 8784;
